import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

const ImageContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-template-areas: stack;
`

const ImagePlaceholder = styled.img`
  grid-area: stack;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity ease 500ms;
  border: none;
`

const MainImage = styled.img`
  grid-area: stack;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity ease;
  transition-duration: 500ms;
  border: none;
  z-index: 10;
`

const Image = ({ file, eager, src, ...props }) => {
  if (src) return <img src={src} {...props} />
  const { cdn, placeholder } = file ?? {}
  if (!cdn) return null
  const mainImageRef = useRef(null)
  const placeholderRef = useRef(null)
  const hasPlaceholder = placeholder?.startsWith('data') ? true : false
  const [loaded, setLoaded] = useState(false)

  const imageLoaded = useCallback(() => {
    setLoaded(true)
    setTimeout(() => {
      placeholderRef.current.remove()
    }, 500)
  }, [mainImageRef, placeholderRef, setLoaded])

  useEffect(() => {
    if (hasPlaceholder) {
      if (mainImageRef.current) {
        if (mainImageRef.current.complete) {
          mainImageRef.current.style.transitionDuration = '0ms'
          placeholderRef.current.remove()
          setLoaded(true)
        } else {
          mainImageRef.current.addEventListener('load', imageLoaded)
        }
      }
      return () => {
        if (mainImageRef.current) {
          mainImageRef.current.removeEventListener('load', imageLoaded)
        }
      }
    }
  }, [mainImageRef])

  return (
    <ImageContainer>
      {hasPlaceholder && (
        <ImagePlaceholder
          show={!loaded}
          src={placeholder}
          ref={placeholderRef}
          {...props}
        />
      )}
      <MainImage
        show={hasPlaceholder ? loaded : true}
        src={cdn}
        ref={mainImageRef}
        {...props}
        loading={eager ? 'eager' : 'lazy'}
      />
    </ImageContainer>
  )
}

Image.propTypes = {
  eager: PropTypes.bool,
  file: PropTypes.object,
  src: PropTypes.string,
  alt: PropTypes.string.isRequired
}

Image.defaultProps = {
  alt: 'klipfolio image'
}

export default Image
